/**
 * Componente Navbar
 * Barra superior com menu hambúrguer, título e perfil do usuário
 */

import React, { useState } from 'react';
import { AppBar, Toolbar, IconButton, Typography, Box, Avatar, useTheme, Menu, MenuItem, Divider } from '@mui/material';
import { Menu as MenuIcon, Logout as LogoutIcon, Person as PersonIcon, People as PeopleIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import PerfilDentista from './PerfilDentista';

const Navbar = ({ onMenuToggle }) => {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isSecretaria = user?.role === 'secretaria';
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [perfilOpen, setPerfilOpen] = useState(false);
  const open = Boolean(anchorEl);

  // Pega a primeira letra do nome para o avatar
  const avatarLetter = user?.nome ? user.nome.charAt(0).toUpperCase() : 'U';

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'background.default',
        color: 'text.primary',
        boxShadow: 'none',
        borderBottom: 'none',
      }}
    >
      <Toolbar sx={{ minHeight: '50px !important', height: '50px' }}>
        {/* Botão Menu Hambúrguer */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={onMenuToggle}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        {/* Título */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 600,
            fontSize: '1.1em',
          }}
        >
          Gerêncie Odonto
        </Typography>

        {/* Espaçador */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Perfil do Usuário */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: '0.9em', display: { xs: 'none', sm: 'block' } }}>
            Olá, {user?.nome || 'Usuário'}!
          </Typography>
          <Avatar
            onClick={handleMenuOpen}
            sx={{
              width: 30,
              height: 30,
              bgcolor: 'primary.main',
              fontSize: '0.875rem',
              fontWeight: 700,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            {avatarLetter}
          </Avatar>
        </Box>

        {/* Menu do Usuário */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              minWidth: 220,
              borderRadius: 2,
              boxShadow: 3,
              p: 0.5,
            }
          }}
        >
          <MenuItem disableRipple sx={{ pointerEvents: 'none', py: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" fontWeight={700} sx={{ letterSpacing: 0.2 }}>
                {user?.nome || 'Usuário'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email || ''}
              </Typography>
            </Box>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />

          <MenuItem
            onClick={() => { handleMenuClose(); navigate('/perfil'); }}
            sx={{ py: 1.2, px: 2, gap: 1 }}
          >
            <PersonIcon sx={{ mr: 1.2, fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body2">Perfil</Typography>
          </MenuItem>

          {user?.role === 'dentista' && (
            <MenuItem
              onClick={() => { handleMenuClose(); navigate('/gerenciar-usuarios'); }}
              sx={{ py: 1.2, px: 2, gap: 1 }}
            >
              <PeopleIcon sx={{ mr: 1.2, fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2">Usuários</Typography>
            </MenuItem>
          )}

          <Divider sx={{ my: 0.5 }} />

          <MenuItem onClick={handleLogout} sx={{ py: 1.2, px: 2, gap: 1 }}>
            <LogoutIcon sx={{ mr: 1.2, fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body2">Sair</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
      {/* Modal de Perfil Dentista */}
      {perfilOpen && (
        <PerfilDentista
          dados={user}
          onSave={(dadosAtualizados) => {
            setPerfilOpen(false);
          }}
          onClose={() => setPerfilOpen(false)}
        />
      )}
    </AppBar>
  );
};

export default Navbar;
